import { Link } from 'react-router-dom';
import { categories } from '@/mocks/categories';

export default function HeroSection() {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleCategorySelect = (url: string, name: string) => {
    setSelectedCategory(name);
    setDropdownOpen(false);
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const selected = categories.find((c) => c.name === selectedCategory);

  return (
    <section className="relative w-full min-h-[620px] md:min-h-[720px] flex items-center justify-center overflow-hidden">
      <div className="absolute inset-0">
        <img
          src="https://readdy.ai/api/search-image?query=Stylish%20modern%20fashion%20retail%20display%20with%20sneakers%20hoodies%20bags%20and%20accessories%20arranged%20on%20clean%20white%20shelving%2C%20warm%20natural%20morning%20sunlight%20streaming%20through%20large%20windows%2C%20soft%20cream%20and%20beige%20neutral%20tones%2C%20editorial%20lifestyle%20product%20photography%2C%20organized%20curated%20collection%2C%20bright%20airy%20minimalist%20boutique%20aesthetic%2C%20professional%20composition%2C%20peaceful%20atmosphere&width=1600&height=900&seq=hero-bg-v2-01&orientation=landscape"
          alt="Basetao Spreadsheet product collection"
          title="Basetao Spreadsheet curated products"
          className="w-full h-full object-cover object-top"
        />
      </div>
      <div className="absolute inset-0 bg-gradient-to-b from-black/35 via-black/25 to-black/40"></div>

      <div className="relative z-10 w-full max-w-[1280px] mx-auto px-6 md:px-10 py-20 md:py-24 text-center">
        <div className="max-w-3xl mx-auto">
          <span className="inline-block px-3 py-1 text-xs font-semibold tracking-wider uppercase text-background-50 bg-primary-500/85 rounded-full mb-5">
            Updated 2026 Resources
          </span>

          <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-heading font-bold text-background-50 leading-tight mb-5">
            Basetao Spreadsheet &mdash; The Ultimate Resource For Smart Shopping, Reviews &amp; Buying Guides
          </h1>

          <p className="text-base md:text-lg text-background-50/85 max-w-2xl mx-auto mb-8 leading-relaxed">
            Discover organized Basetao Spreadsheet resources, product guides, shipping information, warehouse tips, discount updates and expert reviews all in one place.
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-3 mb-10 flex-wrap">
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="w-full sm:w-auto inline-flex items-center justify-center px-7 py-3.5 text-base font-semibold text-background-50 bg-primary-500 hover:bg-primary-600 rounded-md transition-all duration-200 cursor-pointer whitespace-nowrap"
            >
              Explore Basetao Spreadsheet
              <i className="ri-arrow-right-line ml-2"></i>
            </a>
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="w-full sm:w-auto inline-flex items-center justify-center px-7 py-3.5 text-base font-semibold text-background-50 border border-background-50/30 hover:bg-background-50/10 rounded-md transition-all duration-200 cursor-pointer whitespace-nowrap"
            >
              Read Basetao Reviews
            </a>
          </div>

          <div className="flex flex-wrap items-center justify-center gap-4 md:gap-6 text-xs text-background-50/70 mb-10">
            <span className="flex items-center gap-1.5">
              <i className="ri-checkbox-circle-fill text-primary-400 text-sm"></i>
              Updated 2026 Resources
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-checkbox-circle-fill text-primary-400 text-sm"></i>
              Complete Buying Guides
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-checkbox-circle-fill text-primary-400 text-sm"></i>
              Shipping Cost Analysis
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-checkbox-circle-fill text-primary-400 text-sm"></i>
              Safety Research
            </span>
          </div>

          <div ref={dropdownRef} className="relative inline-block w-full max-w-md">
            <button
              onClick={() => setDropdownOpen(!dropdownOpen)}
              className="w-full flex items-center justify-between px-5 py-3.5 bg-background-50/95 backdrop-blur-sm text-foreground-800 rounded-lg border border-background-200/80 hover:border-primary-300 transition-all duration-200 cursor-pointer text-left"
            >
              <span className="flex items-center gap-2">
                <i className="ri-grid-line text-primary-500"></i>
                <span className="text-sm font-medium">
                  {selected ? selected.name : 'Browse Product Categories'}
                </span>
              </span>
              <i className={dropdownOpen ? 'ri-arrow-up-s-line text-foreground-500 transition-transform duration-200' : 'ri-arrow-down-s-line text-foreground-500 transition-transform duration-200'}></i>
            </button>

            {dropdownOpen && (
              <div className="absolute top-full left-0 right-0 mt-2 bg-background-50 rounded-lg border border-background-200/80 shadow-lg overflow-hidden z-50 max-h-[340px] overflow-y-auto">
                {categories.map((cat) => (
                  <button
                    key={cat.name}
                    onClick={() => handleCategorySelect(cat.url, cat.name)}
                    className="w-full flex items-center gap-3 px-5 py-3 text-sm text-foreground-700 hover:bg-secondary-50 hover:text-primary-600 transition-colors duration-150 cursor-pointer text-left"
                  >
                    <i className={`${cat.icon} text-base text-primary-500 w-5 h-5 flex items-center justify-center`}></i>
                    {cat.name}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}